import type { InDmChatItem } from '@/client/components/_molecules/in_dm_chat_row'
import type { NodeContainerData, NodeResponse } from '@/typings/api/instagram_instantwin_node_container'

import {
  PREVIEW_FOLLOW_REPLY,
  PREVIEW_MISSING_DATE_FIRST,
  PREVIEW_MISSING_DATE_SECOND,
  PREVIEW_RESET_NOTICE_MESSAGE,
  PREVIEW_UNFOLLOW_REPLY
} from './previewConstants'

export type PreviewPattern = 'win' | 'lose' | 'unfollow' | 'missing'

export interface BuildPreviewInput {
  containers: NodeContainerData[]
  pattern: PreviewPattern
  answers: Record<number, number>
}

function findRootNode(container: NodeContainerData): NodeResponse | undefined {
  return container.nodes.find(node => node.parentIds.length === 0)
}

function findSelectNode(container: NodeContainerData): Extract<NodeResponse, { type: 'SELECT' }> | undefined {
  return container.nodes.find((node): node is Extract<NodeResponse, { type: 'SELECT' }> => node.type === 'SELECT')
}

function findChildNode(container: NodeContainerData, parentId: number): NodeResponse | undefined {
  return container.nodes.find(node => node.parentIds.includes(parentId))
}

function toMediaType(fileType: string | null | undefined): 'image' | 'video' {
  return fileType?.startsWith('video') ? 'video' : 'image'
}

function nodeToChatItem(node: NodeResponse): InDmChatItem | null {
  switch (node.type) {
    case 'TEXT':
      return { type: 'message', text: node.text ?? '', side: 'left' }
    case 'MEDIA': {
      if (!node.media) return null
      return { type: 'media', url: node.media.url ?? '', mediaType: toMediaType(node.media.fileType) }
    }
    case 'CARD': {
      const card = node.card
      if (!card) return null
      return {
        type: 'card',
        title: card.title,
        subtitle: card.subtitle,
        url: card.cardUrl,
        mediaUrl: card.url ?? '',
        // url が空のボタンは href="" のリンクになり誤動作するため除外する
        cardButtons: card.buttons.flatMap(button => (button.url ? [{ name: button.name, url: button.url }] : []))
      }
    }
    default:
      return null
  }
}

function appendNodeChain(
  items: InDmChatItem[],
  container: NodeContainerData,
  startNode: NodeResponse | undefined,
  answers: Record<number, number>
): void {
  let current = startNode
  const visited = new Set<number>()
  while (current && !visited.has(current.id)) {
    visited.add(current.id)
    if (current.type === 'SELECT') {
      items.push({ type: 'message', text: current.text ?? '', side: 'left' })
      const options = current.selectOption
      // 選択肢が無い・回答テキストが無い不完全シナリオでは回答吹き出しを出さずチェーンを打ち切る
      if (options.length === 0) break
      const selectedId = answers[current.id]
      const selected = options.find(option => option.id === selectedId) ?? options[0]
      if (!selected.text) break
      items.push({ type: 'message', text: selected.text, side: 'right' })
      current = findChildNode(container, selected.id)
      continue
    }
    const item = nodeToChatItem(current)
    if (item) items.push(item)
    current = findChildNode(container, current.id)
  }
}

function appendFollowCheck(items: InDmChatItem[], container: NodeContainerData, reply: string): void {
  const select = findSelectNode(container)
  if (!select) return
  items.push({ type: 'message', text: select.text ?? '', side: 'left' })
  items.push({ type: 'message', text: reply, side: 'right' })
  const repliedOption = select.selectOption.find(option => option.text === reply)
  if (!repliedOption) return
  appendNodeChain(items, container, findChildNode(container, repliedOption.id), {})
}

function appendUnfollow(items: InDmChatItem[], container: NodeContainerData | undefined): void {
  if (!container) return
  const select = findSelectNode(container)
  if (!select) return
  items.push({ type: 'message', text: select.text ?? '', side: 'left' })
  items.push({ type: 'message', text: PREVIEW_UNFOLLOW_REPLY, side: 'right' })
  const noOption = select.selectOption.find(option => option.text === PREVIEW_UNFOLLOW_REPLY)
  if (!noOption) return
  appendNodeChain(items, container, findChildNode(container, noOption.id), {})
}

function appendChainUntilSelect(
  items: InDmChatItem[],
  container: NodeContainerData,
  startNode: NodeResponse | undefined
): boolean {
  let current = startNode
  const visited = new Set<number>()
  while (current && !visited.has(current.id)) {
    visited.add(current.id)
    if (current.type === 'SELECT') {
      if (!current.text) return false
      items.push({ type: 'message', text: current.text, side: 'left' })
      return true
    }
    const item = nodeToChatItem(current)
    if (item) items.push(item)
    current = findChildNode(container, current.id)
  }
  return false
}

function appendMissing(
  items: InDmChatItem[],
  followCheckContainer: NodeContainerData | undefined,
  freeContainer: NodeContainerData | undefined
): void {
  const followSelectText = followCheckContainer ? findSelectNode(followCheckContainer)?.text : undefined
  if (followSelectText) {
    items.push({ type: 'date', datetime: PREVIEW_MISSING_DATE_FIRST })
    items.push({ type: 'message', text: followSelectText, side: 'left' })
    items.push({ type: 'date', datetime: PREVIEW_MISSING_DATE_SECOND })
    items.push({ type: 'message', text: PREVIEW_RESET_NOTICE_MESSAGE, side: 'left' })
    return
  }

  if (!freeContainer) return
  const buffer: InDmChatItem[] = [{ type: 'date', datetime: PREVIEW_MISSING_DATE_FIRST }]
  const reachedSelect = appendChainUntilSelect(buffer, freeContainer, findRootNode(freeContainer))
  if (!reachedSelect) return
  buffer.push({ type: 'date', datetime: PREVIEW_MISSING_DATE_SECOND })
  buffer.push({ type: 'message', text: PREVIEW_RESET_NOTICE_MESSAGE, side: 'left' })
  items.push(...buffer)
}

function appendLotteryResult(items: InDmChatItem[], container: NodeContainerData, pattern: 'win' | 'lose'): void {
  const root = findRootNode(container)
  if (!root) return
  const branchType = pattern === 'win' ? 'LOTTERY_WIN' : 'LOTTERY_LOSE'
  const branch = container.nodes.find(node => node.type === branchType && node.parentIds.includes(root.id))
  if (!branch) return
  appendNodeChain(items, container, findChildNode(container, branch.id), {})
}

export function buildPreviewChatItems(input: BuildPreviewInput): InDmChatItem[] {
  const { containers, pattern, answers } = input
  const items: InDmChatItem[] = []
  const sortedContainers = [...containers].sort((a, b) => a.stepOrder - b.stepOrder)

  if (pattern === 'unfollow') {
    appendUnfollow(
      items,
      sortedContainers.find(container => container.type === 'FOLLOW_CHECK')
    )
    return items
  }

  if (pattern === 'missing') {
    appendMissing(
      items,
      sortedContainers.find(container => container.type === 'FOLLOW_CHECK'),
      sortedContainers.find(container => container.type === 'FREE')
    )
    return items
  }

  for (const container of sortedContainers) {
    switch (container.type) {
      case 'FOLLOW_CHECK':
        appendFollowCheck(items, container, PREVIEW_FOLLOW_REPLY)
        break
      case 'FREE':
        appendNodeChain(items, container, findRootNode(container), answers)
        break
      case 'LOTTERY_LOADING':
        appendNodeChain(items, container, findRootNode(container), answers)
        break
      case 'LOTTERY_CONTAINER':
        appendLotteryResult(items, container, pattern)
        break
      default:
        break
    }
  }

  return items
}
